import { useMemo, useCallback } from 'react';
import isEqual from 'lodash/isEqual';
import {
  useDataFeedWizardStore,
  useInstrumentItems,
  useOTCItems,
} from './dataFeedWizardStore';
import { StepKey } from './types';

/**
 * Hook for tracking dirty state in DataFeedWizard steps.
 * Compares current step data with pristine (initial/last-saved) state.
 * Uses memoized comparisons to avoid unnecessary re-renders.
 */
export const useDirtyTracking = () => {
  const { pristineState, configureMappingData, linkAccountsData } =
    useDataFeedWizardStore();

  // Get current items using stable selector hooks (prevents infinite re-renders)
  const instrumentItems = useInstrumentItems();
  const otcItems = useOTCItems();

  const isStep1Dirty = useMemo(() => {
    if (!pristineState.step1) return true; // No pristine = needs save
    return !isEqual(pristineState.step1, configureMappingData);
  }, [pristineState.step1, configureMappingData]);

  const isStep2Dirty = useMemo(() => {
    if (!pristineState.step2) return true; // No pristine = needs save
    return !isEqual(pristineState.step2, linkAccountsData.mappings);
  }, [pristineState.step2, linkAccountsData.mappings]);

  const isStep3Dirty = useMemo(() => {
    if (!pristineState.step3) return true; // No pristine = needs save
    return !isEqual(pristineState.step3, instrumentItems);
  }, [pristineState.step3, instrumentItems]);

  const isStep4Dirty = useMemo(() => {
    if (!pristineState.step4) return true; // No pristine = needs save
    return !isEqual(pristineState.step4, otcItems);
  }, [pristineState.step4, otcItems]);

  const isDirty = useCallback(
    (step: StepKey): boolean => {
      switch (step) {
        case 'step1':
          return isStep1Dirty;
        case 'step2':
          return isStep2Dirty;
        case 'step3':
          return isStep3Dirty;
        case 'step4':
          return isStep4Dirty;
        default:
          return false;
      }
    },
    [isStep1Dirty, isStep2Dirty, isStep3Dirty, isStep4Dirty]
  );

  const isAnyDirty =
    isStep1Dirty || isStep2Dirty || isStep3Dirty || isStep4Dirty;

  return {
    isDirty,
    isStep1Dirty,
    isStep2Dirty,
    isStep3Dirty,
    isStep4Dirty,
    isAnyDirty,
  };
};
